// Aktive Klubb-bestillinger i trykklisten (/service), mellom trykkjobbene og klubbordre fra
// nettbutikken (vinnsel, 02.11): ordrer i butikken med status «AktivKLUBB», så klubbansvarlig
// snapper dem opp. De står også i ordrelisten. En bestilling som skal trykkes, overføres til trykk
// på ordren (src/lib/ordreTrykk.js); da vises trykket her, og begge avsluttes sammen.
import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { parse, isValid, format } from 'date-fns';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL;

function tilDato(s) {
  if (!s) return null;
  const egen = parse(s, 'd.M.yyyy, HH:mm:ss', new Date());
  if (isValid(egen)) return egen;
  const iso = new Date(s);
  return isValid(iso) ? iso : null;
}
const datoTekst = (s) => { const d = tilDato(s); return d ? format(d, 'd.M.yyyy') : ''; };

export default function AktiveKlubbbestillinger({ butikkid }) {
  const navigate = useNavigate();
  const [ordrer, setOrdrer] = useState(null);
  const [feil, setFeil] = useState('');

  useEffect(() => {
    let avbrutt = false;
    fetch(`${API_BASE_URL}/orders`)
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((data) => {
        if (avbrutt) return;
        // Eldste først: de som har ventet lengst, øverst.
        setOrdrer(data
          .filter((o) => o.Status === 'AktivKLUBB' && o.butikkid === butikkid)
          .sort((a, b) => (tilDato(a.RegistrertDato) || 0) - (tilDato(b.RegistrertDato) || 0)));
      })
      .catch((e) => {
        console.error('Feil ved henting av klubbbestillinger:', e.message);
        if (!avbrutt) { setFeil('Klarte ikke å hente klubbbestillingene. Last siden på nytt om litt.'); setOrdrer([]); }
      });
    return () => { avbrutt = true; };
  }, [butikkid]);

  return (
    <section aria-labelledby="tl-klubb" className="space-y-3">
      <h2 id="tl-klubb" className="flex flex-wrap items-center gap-2 text-lg font-bold text-slate-900">
        Aktive Klubb-bestillinger
        {ordrer && <span className="rounded-full bg-violet-100 px-2.5 py-0.5 text-sm font-semibold text-violet-900">{ordrer.length} aktive</span>}
      </h2>
      <p className="text-slate-700">
        Ordrer med status «Aktiv Klubb-bestilling». Skal varene trykkes med logo, åpne ordren og velg «Overfør til trykk».
      </p>
      {feil && <p role="alert" className="rounded-xl border border-red-200 bg-red-50 px-3 py-1.5 text-red-800">{feil}</p>}
      {!ordrer ? (
        <p className="rounded-xl border border-dashed border-slate-300 px-3 py-5 text-slate-700">Henter klubbbestillinger …</p>
      ) : ordrer.length === 0 ? (
        !feil && <p className="rounded-xl border border-dashed border-slate-300 px-3 py-5 text-slate-700">Ingen aktive Klubb-bestillinger.</p>
      ) : (
        <div tabIndex={0} role="region" aria-label="Aktive Klubb-bestillinger, kan rulles sidelengs"
          className="overflow-x-auto rounded-2xl border border-slate-200 bg-white focus:outline-none focus-visible:ring-4 focus-visible:ring-indigo-600/50">
          <table className="min-w-full text-left">
            <thead className="bg-slate-100 text-sm font-semibold text-slate-700">
              <tr>
                <th scope="col" className="px-3 py-1.5">Nr.</th>
                <th scope="col" className="px-3 py-1.5">Vare</th>
                <th scope="col" className="hidden px-3 py-1.5 md:table-cell">Str.</th>
                <th scope="col" className="hidden px-3 py-1.5 md:table-cell">Farge</th>
                <th scope="col" className="px-3 py-1.5">Trykk</th>
                <th scope="col" className="hidden px-3 py-1.5 md:table-cell">Ansatt</th>
                <th scope="col" className="hidden px-3 py-1.5 md:table-cell">Registrert</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-200 text-slate-800">
              {ordrer.map((o) => (
                <tr key={o._id} className="cursor-pointer align-top hover:bg-violet-50/50" onClick={() => navigate(`/order-details/${o._id}`)}>
                  <td className="px-3 py-1.5 font-mono text-sm">
                    <Link to={`/order-details/${o._id}`} onClick={(e) => e.stopPropagation()}
                      className="font-semibold text-indigo-700 underline underline-offset-2 hover:text-indigo-900">
                      {o.ordreid}
                    </Link>
                  </td>
                  <td className="min-w-[12rem] px-3 py-1.5">
                    <span className="block font-semibold [overflow-wrap:anywhere]">{[o.Varemerke, o.Produkt].filter(Boolean).join(' ') || 'Uten navn'}</span>
                    <span className="block text-sm text-slate-600 md:hidden">{[o.Størrelse, o.Farge, o.Ansatt].filter(Boolean).join(' · ')}</span>
                    {o.Kommentar && <span className="block text-sm text-slate-600 line-clamp-2 [overflow-wrap:anywhere]">{o.Kommentar}</span>}
                  </td>
                  <td className="hidden px-3 py-1.5 md:table-cell">{o.Størrelse}</td>
                  <td className="hidden px-3 py-1.5 md:table-cell">{o.Farge}</td>
                  <td className="whitespace-nowrap px-3 py-1.5">
                    {o.trykk?.id ? (
                      <Link to={`/service-details-club/${o.trykk.id}`} onClick={(e) => e.stopPropagation()}
                        className="font-semibold text-indigo-700 underline underline-offset-2 hover:text-indigo-900">
                        Trykk {o.trykk.serviceid}
                      </Link>
                    ) : <span className="text-slate-600">Ikke overført</span>}
                  </td>
                  <td className="hidden px-3 py-1.5 md:table-cell">{o.Ansatt}</td>
                  <td className="hidden whitespace-nowrap px-3 py-1.5 text-sm md:table-cell">{datoTekst(o.RegistrertDato)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
